import { useState, useMemo, ChangeEvent } from "react";
import styled from "styled-components";
import { useTranslation } from "@coworker/locales";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useMyStore } from "../../../core/auth/useLoggedInUser";
import { useNotificationsByStore } from "../hooks/useNotificationsByStore";
import { useMediasByStoreId } from "../hooks/useMediasByStoreId";
import { MediasViewSearchListDesktop } from "../Organisms/MediasViewSearchListDesktop";
import { MessageAndButton } from "../Molecules/MessageAndButton";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { Media, MediasResponse } from "../types/media";
import { SignedUrlRelation } from "../types/images";
import { MessageModal } from "../Organisms/Modals/MessageModal";
import { useUpdateMediaWithId } from "../hooks/useUpdateMedia";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import { MediasViewListDesktop } from "../Organisms/MediasViewListDesktop";
import { useSearchArticlesInMedias } from "../hooks/useSearchArticlesInMedias";
import { useMatchingMedias } from "../../../hooks/useMatchingMedias";
import { MediasViewTabs } from "../Molecules/MediasViewTabs";
import { useFilteredMedias } from "../hooks/useFilteredMedias";
import { FilterPanel } from "@coworker/apprestructured/src/shared/filters/components/FilterPanel";
import { useMediaOverviewFilters } from "../hooks/useMediaOverviewFilters";
import { mapUniqueMediaAreas } from "../Helpers/mapUniqueMediaAreas";
import { NoResults } from "../Molecules/NoResults";
import { useTaskLinks } from "../hooks/useTasksByTaskLinks";
import { TaskListingTypes } from "../constants";
import { useSignedUrls } from "../hooks/useSignedUrls";
import { AskFixa } from "../AskFixaChat/AskFixa";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

const ScrollContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 0px 64px 64px 64px;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 40px;
  padding-bottom: 40px;
`;

const HeaderTitleText = styled.div`
  font-size: 24px;
  font-weight: 700;
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

const TabsWrapper = styled.div`
  padding-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
`;

export function MediasViewDesktop() {
  const [showModalMessage, setShowModalMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [displayedQuery, setDisplayedQuery] = useState("");
  const enableMediaAssistant = useFlag(FLAGS.ENABLE_MEDIA_AI_ASSISTANT);
  const [selectedMediaTypeTab, setSelectedMediaTypeTab] =
    useState<string>("allMedia");

  const { t } = useTranslation();
  const navigate = useFixaNavigate();
  const myStore = useMyStore();
  const {
    data: mediasResponse,
    isError,
    isLoading: isMediasLoading,
  } = useMediasByStoreId(myStore.storeId);

  const mainImageFileNames = useMemo(
    () =>
      (mediasResponse?.medias
        ?.map((m) => m.mainImageFileName)
        .filter(Boolean) as string[]) ?? [],
    [mediasResponse?.medias]
  );

  const { data: signedUrlRelationsResponse, isLoading: isLoadingSignedUrls } =
    useSignedUrls(mainImageFileNames, "large");

  const signedUrlRelations: SignedUrlRelation[] =
    isLoadingSignedUrls || !signedUrlRelationsResponse
      ? []
      : signedUrlRelationsResponse;

  const mediasWithResolvedMainImageUrl = useMemo(() => {
    return (mediasResponse?.medias ?? []).map((media) => {
      if (!media.mainImageFileName) return { ...media, mainImageUrl: "" };
      const relation = signedUrlRelations.find((r) =>
        media.mainImageFileName!.includes(r.fileName)
      );
      if (!relation) return { ...media, mainImageUrl: "" };
      return { ...media, mainImageUrl: relation.signedUrl };
    });
  }, [mediasResponse?.medias, signedUrlRelations]);

  const { data: notifications } = useNotificationsByStore(
    myStore.storeId,
    myStore.countryId
  );

  const updateMediaMutation = useUpdateMediaWithId();

  const uniqueMediaAreas = mapUniqueMediaAreas(mediasResponse?.medias ?? []);
  const mediaOverviewFilters = useMediaOverviewFilters(
    uniqueMediaAreas,
    notifications?.uniqueEdsDates ?? []
  );
  const { data: articles } = useSearchArticlesInMedias(
    myStore.storeId,
    searchQuery,
    mediaOverviewFilters.filterValues.find((f) => f.filterId === "articleHfb")
      ?.value as string | undefined
  );
  const tasks = useTaskLinks(
    notifications?.taskLinks || [],
    TaskListingTypes.Ongoing
  );
  const { data: articlesFromHfb, isLoading: isLoadingArticlesHfb } =
    useSearchArticlesInMedias(
      myStore.storeId,
      undefined,
      mediaOverviewFilters.filterValues.find((f) => f.filterId === "articleHfb")
        ?.value as string | undefined
    );

  const filteredMedias = useFilteredMedias(
    mediasWithResolvedMainImageUrl,
    notifications?.alerts,
    tasks,
    selectedMediaTypeTab,
    mediaOverviewFilters.filterValues,
    articlesFromHfb
  );
  const matchingMedias = useMatchingMedias(filteredMedias, searchQuery);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const sanitizedInput = inputValue.replace(/[.,-]/g, "");
    setDisplayedQuery(inputValue);
    setSearchQuery(sanitizedInput);
  };

  const onClear = () => {
    setDisplayedQuery("");
    setSearchQuery("");
  };

  const onSaveMedia = async (media: Media) => {
    try {
      const { nbrArticles: nbr_articles, pieces, ...saveMedia } = media;
      await updateMediaMutation.mutateAsync(saveMedia);
    } catch (error) {
      console.log(error);
      setShowModalMessage(t("couldNotUpdateRoomTryAgainString"));
    }
  };

  // No medias
  if (mediasResponse?.medias && mediasResponse.medias.length === 0) {
    return (
      <>
        <MessageAndButton
          keyForMessageText={"noMediasCreatedString"}
          keyForButtonText={"addMediaString"}
          buttonAction={async () => {
            navigate(`/mediamaintenance/create`);
          }}
        />
      </>
    );
  }

  if (isError) {
    return (
      <MessageAndButton
        keyForMessageText={"couldNotLoadMediasString"}
        keyForButtonText={"tryAgainString"}
        buttonAction={async () => {
          window.location.reload();
        }}
      />
    );
  }

  const doSearch = searchQuery.length > 1;

  return (
    <>
      <ScrollContainer>
        <ToolbarContainer>
          <HeaderTitleText>{t("mediaMaintenanceString")}</HeaderTitleText>
          <FixaButton
            aria-label={t("addMediaString")}
            text={t("addMediaString")}
            type="primary"
            data-testid="addRoomSettingsButton"
            onClick={() => {
              navigate(`/mediamaintenance/create`);
            }}
          />
        </ToolbarContainer>

        <FilterWrapper>
          <div>
            <FilterPanel
              filterValues={mediaOverviewFilters.filterValues}
              setFilterValues={mediaOverviewFilters.setFilterValues}
              useFiltersFactory={mediaOverviewFilters.useFiltersFactory}
              feature="media"
            />
          </div>
        </FilterWrapper>
        {enableMediaAssistant && <AskFixa />}

        <TabsWrapper>
          <MediasViewTabs
            onTabChange={setSelectedMediaTypeTab}
            activeTab={selectedMediaTypeTab}
            allMedias={mediasWithResolvedMainImageUrl}
            filterValues={mediaOverviewFilters.filterValues}
            query={searchQuery}
            alerts={notifications?.alerts ?? []}
            tasks={tasks}
            loading={isMediasLoading || isLoadingArticlesHfb}
            articlesFromHfb={articlesFromHfb}
          />
          <FixaSearch
            id="media-search"
            value={displayedQuery}
            onChange={onChange}
            onClear={onClear}
            placeholder={t("mediasViewSearch")}
            ariaLabel={t("mediasViewSearch")}
            style={{ width: "310px" }}
          />
        </TabsWrapper>

        {doSearch ? (
          <MediasViewSearchListDesktop
            mediasResponse={
              {
                medias: matchingMedias,
                children: mediasResponse?.children,
              } as MediasResponse
            }
            alerts={notifications?.alerts}
            taskLinks={tasks.taskLinks}
            onSaveMedia={onSaveMedia}
            storeId={myStore.storeId}
            articles={articles ?? undefined}
          />
        ) : (
          <MediasViewListDesktop
            mediasResponse={
              {
                medias: filteredMedias,
                children: mediasResponse?.children,
              } as MediasResponse
            }
            alerts={notifications?.alerts}
            taskLinks={tasks.taskLinks}
            onSaveMedia={onSaveMedia}
            loading={isMediasLoading || isLoadingArticlesHfb}
            filterValues={mediaOverviewFilters.filterValues}
          />
        )}
        <NoResults
          isSearch={searchQuery.length > 0}
          isFilters={mediaOverviewFilters.filterValues.length > 0}
          mediasCount={filteredMedias?.length ?? 0}
          matchingMediasCount={matchingMedias?.length ?? 0}
          articlesCount={articles?.length ?? 0}
          mediaType={selectedMediaTypeTab}
          query={searchQuery}
          clearFilters={() => mediaOverviewFilters.setFilterValues([])}
          clearSearch={onClear}
          setTabToAllMedias={() => setSelectedMediaTypeTab("allMedia")}
        />
      </ScrollContainer>

      {!!showModalMessage && (
        <MessageModal
          message={showModalMessage}
          closeModal={() => {
            setShowModalMessage("");
          }}
        />
      )}
    </>
  );
}
